// src/pages/StudentRegistration.jsx
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import SummaryApi from '../common/index';

const StudentRegistration = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const encodedStudentId = queryParams.get('id');
  const isEditMode = !!encodedStudentId;
  const studentId = encodedStudentId ? atob(encodedStudentId) : null;

  const [formData, setFormData] = useState({
    full_name: '',
    category: '',
    religion: '',
    caste: '',
    dob: '',
    gender: '',
    mother_name: '',
    mobile_number: '',
    parent_mobile_number: '',
    email: '',
    aadhar: '',
    photograph: null,
    aadhar_image: null,  
    btech_preffered_branch: '',
    address_line1: '',
    state: '',
    city: '',
    pincode: '',
    district: '',
    tenth_board: '',
    tenth_year_passing: '',
    tenth_percentage: '',
    twelfth_board_name: '',
    twelfth_year_passing: '',
    twelfth_percentage_cgpa: '',
    twelfth_branch: '',
    ssc_memo: null,
    inter_first_year_memo: null,
    inter_second_year_memo: null,
    entrance_exam_name: '',
    declaration_agreed: false,
    is_paid: '0',
  });

  const [photoPreview, setPhotoPreview] = useState(null);
  const [aadharPreview, setAadharPreview] = useState(null);
  const [sscMemoPreview, setSscMemoPreview] = useState(null);
  const [firstMemoPreview, setFirstMemoPreview] = useState(null);
  const [secondMemoPreview, setSecondMemoPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isRegistered, setIsRegistered] = useState(isEditMode);
  const [studentIdStored, setStudentIdStored] = useState(studentId);

  const photoRef = useRef();
  const aadharRef = useRef();
  const sscMemoRef = useRef();
  const firstYearMemoRef = useRef();
  const secondYearMemoRef = useRef();

  useEffect(() => {
    if (isEditMode && studentId) {
      fetchStudentData();
    }
    return () => {
      [photoPreview, aadharPreview, sscMemoPreview, firstMemoPreview, secondMemoPreview].forEach(preview => {
        if (preview && preview.startsWith('blob:')) {
          URL.revokeObjectURL(preview);
        }
      });
    };
  }, [isEditMode, studentId]);

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      const res = await fetch(SummaryApi.getstudentByid.url, {
        method: SummaryApi.getstudentByid.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_id: studentId }),
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const result = await res.json();
      if (result.status === true && result.data) {
        const student = result.data;
        const formattedDob = student.dob
          ? new Date(student.dob).toISOString().split('T')[0]
          : '';
        setFormData({
          full_name: student.full_name || '',
          category: student.category || '',
          religion: student.religion || '',
          caste: student.caste || '',
          dob: formattedDob,
          gender: student.gender || '',
          mother_name: student.mother_name || '',
          mobile_number: student.mobile_number || '',
          parent_mobile_number: student.parent_mobile_number || '',
          email: student.email || '',
          aadhar: student.aadhar || '',
          photograph: null,
          btech_preffered_branch: student.btech_preffered_branch || '', 
          address_line1: student.address_line1 || '',
          state: student.state || '',
          city: student.city || '',
          pincode: student.pincode || '',
          district: student.district || '',
          tenth_board: student.tenth_board || '',
          tenth_year_passing: student.tenth_year_passing || '',
          tenth_percentage: student.tenth_percentage || '',
          twelfth_board_name: student.twelfth_board_name || '',
          twelfth_year_passing: student.twelfth_year_passing || '',
          twelfth_percentage_cgpa: student.twelfth_percentage_cgpa || '',
          twelfth_branch: student.twelfth_branch || '',
          ssc_memo: null,
          inter_first_year_memo: null,
          inter_second_year_memo: null,
          entrance_exam_name: student.entrance_exam_name || '',
          declaration_agreed: true,
          is_paid: student.is_paid || '0',
        });
        const baseUrl = SummaryApi.Images.url;
        if (student.photograph) setPhotoPreview(`${baseUrl}${student.photograph}`);
        if (student.aadhar_image) setAadharPreview(`${baseUrl}${student.aadhar_image}`);
        if (student.ssc_memo && student.ssc_memo !== "N/A") setSscMemoPreview(`${baseUrl}${student.ssc_memo}`);
        if (student.inter_first_year_memo) setFirstMemoPreview(`${baseUrl}${student.inter_first_year_memo}`);
        if (student.inter_second_year_memo) setSecondMemoPreview(`${baseUrl}${student.inter_second_year_memo}`);
        setIsRegistered(true);
      } else {
        toast.error(result.message || "Failed to load student data");
      }
    } catch (err) {
      console.error("Fetch student error:", err);
      toast.error("Error loading student details.");
    } finally {
      setLoading(false);
    }
  };

  const validateName = (name) => {
    const nameRegex = /^[A-Za-z\s]+$/;
    return nameRegex.test(name);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const validateNumbersOnly = (value) => {
    const numbersRegex = /^\d+$/;
    return numbersRegex.test(value);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      if (name === 'full_name' || name === 'mother_name' || name === 'city' || name === 'district' || name === 'state') {
        if (value === '' || /^[A-Za-z\s]*$/.test(value)) {
          setFormData((prev) => ({ ...prev, [name]: value }));
        }
      }
      else if (name === 'tenth_year_passing' || name === 'twelfth_year_passing') {
        if (value === '' || (validateNumbersOnly(value) && value.length <= 4)) {
          setFormData((prev) => ({ ...prev, [name]: value }));
        }
      }
      else if (name === 'mobile_number' || name === 'parent_mobile_number' || name === 'aadhar') {
        if (value === '' || (validateNumbersOnly(value) && value.length <= 12)) {
          setFormData((prev) => ({ ...prev, [name]: value }));
        }
      }
      else if (name === 'tenth_percentage' || name === 'twelfth_percentage_cgpa') {
        const percentageRegex = /^\d*\.?\d*$/;
        if (value === '' || percentageRegex.test(value)) {
          setFormData((prev) => ({ ...prev, [name]: value }));
        }
      }
      else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    }
  };

  const isFormValid = () => {
    return validateForm() === "";
  };

  const handleNumberOnly = (e) => {
    const char = e.key;
    if (!/[0-9]/.test(char) && char !== 'Backspace' && char !== 'Delete' && char !== 'Tab' && char !== 'ArrowLeft' && char !== 'ArrowRight') {
      e.preventDefault();
    }
  };

  const handleFileChange = (e, fieldName, setPreview) => {
    const file = e.target.files[0];
    if (!file) return;
    setFormData((prev) => ({ ...prev, [fieldName]: file }));
    if (file.type.startsWith('image/')) {
      const previewUrl = URL.createObjectURL(file);
      setPreview(previewUrl);
    } else {
      setPreview('document-icon');
    }
  };

  const getFilePreviewContent = (previewUrl, field, fileName = '') => {
    if (!previewUrl) return null;
    if (previewUrl === 'document-icon') {
      return (
        <div className="flex flex-col items-center gap-2">
          <div className="w-20 h-28 bg-gradient-to-b from-red-50 to-red-100 rounded-lg border border-red-200 flex items-center justify-center shadow-sm relative">
            <span className="text-5xl">📄</span>
            <div className="absolute bottom-1.5 right-1.5 bg-white/80 text-[10px] px-1.5 py-0.5 rounded font-medium text-red-700">
              PDF
            </div>
          </div>
          {fileName && (
            <span className="text-xs text-gray-600 max-w-[180px] truncate text-center">
              {fileName}
            </span>
          )}
          <p className="text-xs text-gray-500">Document uploaded</p>
        </div>
      );
    }
    const isBlob = previewUrl.startsWith('blob:');
    let extension = isBlob ? 'image' : (previewUrl.split('.').pop()?.toLowerCase() || '');
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'image'].includes(extension);
    if (isImage) {
      return (
        <img
          src={previewUrl}
          alt={`${field} preview`}
          className="max-h-48 max-w-full object-contain rounded border border-gray-200 shadow-sm"
        />
      );
    }
    return (
      <div className="flex flex-col items-center gap-2">
        <div className="w-20 h-28 bg-gradient-to-b from-red-50 to-red-100 rounded-lg border border-red-200 flex items-center justify-center shadow-sm relative">
          <span className="text-5xl">📄</span>
          <div className="absolute bottom-1.5 right-1.5 bg-white/80 text-[10px] px-1.5 py-0.5 rounded font-medium text-red-700">
            {extension.toUpperCase() || 'FILE'}
          </div>
        </div>
        <p className="text-xs text-gray-500">Document uploaded</p>
      </div>
    );
  };

  const validateForm = () => {
    if (!formData.full_name.trim()) return "Full Name is required";
    if (!validateName(formData.full_name)) return "Full Name should contain only letters and spaces";
    
    if (!formData.category) return "Category is required";
    if (!formData.religion) return "Religion is required";
    if (!formData.dob) return "Date of Birth is required";
    if (!formData.gender) return "Gender is required";
    
    if (!formData.mother_name.trim()) return "Mother's Name is required";
    if (!validateName(formData.mother_name)) return "Mother's Name should contain only letters and spaces";
    if (!formData.btech_preffered_branch) return "Preferred Branch is required";

    if (!formData.mobile_number) return "Mobile number is required";
    if (formData.mobile_number.length !== 10) return "Mobile number must be exactly 10 digits";
    if (!validateNumbersOnly(formData.mobile_number)) return "Mobile number must contain only digits";
    
    if (!formData.parent_mobile_number) return "Parent's mobile number is required";
    if (formData.parent_mobile_number.length !== 10) return "Parent's mobile number must be exactly 10 digits";
    if (!validateNumbersOnly(formData.parent_mobile_number)) return "Parent's mobile number must contain only digits";
    
    if (!formData.email.trim()) return "Email is required";
    if (!validateEmail(formData.email)) return "Please enter a valid email address";                                                                                                    

if (!formData.photograph && !photoPreview)
  return "Student photograph is required";

if (!formData.aadhar_image && !aadharPreview)
  return "Aadhaar card image is required";

if (!formData.ssc_memo && !sscMemoPreview)
  return "SSC Marks Memo is required";

if (!formData.inter_first_year_memo && !firstMemoPreview)
  return "Inter 1st Year Marks Memo is required";

if (!formData.inter_second_year_memo && !secondMemoPreview)
  return "Inter 2nd Year Marks Memo is required";
    if (!formData.aadhar) return "Aadhaar number is required";
    if (formData.aadhar.length !== 12) return "Aadhaar number must be exactly 12 digits";
    if (!validateNumbersOnly(formData.aadhar)) return "Aadhaar number must contain only digits";
    if (!formData.address_line1.trim()) return "Correspondence Address is required";
    if (!formData.district.trim()) return "District is required";
    if (!validateName(formData.district)) return "District should contain only letters and spaces";
    if (!formData.city.trim()) return "City is required";
    if (!validateName(formData.city)) return "City should contain only letters and spaces";
    if (!formData.state.trim()) return "State is required";
    if (!validateName(formData.state)) return "State should contain only letters and spaces";
    if (!formData.caste.trim()) return "Caste is required";
    if (!formData.pincode.trim()) return "PIN Code is required";
    if (!formData.tenth_board.trim()) return "10th Board is required";
    if (!formData.tenth_year_passing.trim()) return "10th Year of Passing is required";
    if (!formData.tenth_percentage.trim()) return "10th Percentage is required";
    
    if (!formData.twelfth_board_name.trim()) return "12th Board is required";
    if (!formData.twelfth_year_passing.trim()) return "12th Year of Passing is required";
    if (!formData.twelfth_percentage_cgpa.trim()) return "12th Percentage/CGPA is required";
    if (!formData.twelfth_branch.trim()) return "12th Branch is required";
    if (!formData.entrance_exam_name.trim()) return "Entrance Exam Name is required";
    if (!formData.declaration_agreed) return "You must agree to the declaration";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errorMsg = validateForm();
    if (errorMsg) {
      toast.error(errorMsg);
      return;
    }

    setLoading(true);

    try {
      const adminId = sessionStorage.getItem('admin_id');
      if (!adminId) {
        toast.error("Admin not logged in. Please login again.");
        return;
      }

      const submitData = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        if (['photograph', 'aadhar_image', 'ssc_memo', 'inter_first_year_memo', 'inter_second_year_memo'].includes(key)) {
          if (value instanceof File) {
            submitData.append(key, value);
          }
        } else if (value !== null && value !== undefined && value !== '') {
          submitData.append(key, value);
        }
      });

      submitData.append('admin_id', adminId);
      submitData.append('created_date', new Date().toISOString().slice(0, 19).replace('T', ' '));
      submitData.append('is_active', '1');
      submitData.append('is_paid', formData.is_paid || '0');

      let res, result;

      if (!isRegistered) {
        res = await fetch(SummaryApi.studentRegistration.url, {
          method: SummaryApi.studentRegistration.method,
          body: submitData,
        });
        result = await res.json();

        if (result.success || result.student_id) {
          toast.success("Student registered successfully!");
          const newStudentId = result.student_id;
          setStudentIdStored(newStudentId);
          setIsRegistered(true);
          const encodedId = btoa(newStudentId.toString());
          const encodedName = btoa(formData.full_name.trim());
          navigate(`/create-studentfee?id=${encodedId}&name=${encodedName}`);
        } else {
          toast.error(result.message || "Registration failed");
        }
      } else {
        submitData.append('student_id', studentIdStored || studentId);

        res = await fetch(SummaryApi.updatestudentbyid.url, {
          method: SummaryApi.updatestudentbyid.method,
          body: submitData,
        });
        result = await res.json();

        if (result.status === true || result.success === true) {
          toast.success(result.message || "Student updated successfully!");
          navigate("/student-report");
        } else {
          toast.error(result.message || "Update failed");
        }
      }
    } catch (err) {
      console.error("Submit error:", err);
      toast.error("Error during operation");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "block w-full border border-gray-300 rounded px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";
  const disabledInputClass = `${inputClass} bg-gray-100 cursor-not-allowed`;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="bg-white shadow-lg rounded-xl p-6 md:p-8 border border-gray-200 relative">
        <h2 className="text-3xl font-bold text-center text-blue-900 mb-10">
          {isEditMode || isRegistered ? "Edit Student Details" : "Student Admission Form"}
        </h2>

        {loading && <div className="text-center py-4">Loading...</div>}

        <form onSubmit={handleSubmit} className="space-y-12" noValidate>

          {/* PREFERRED BRANCH */}
          <section>
            <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Course Applying For</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-xl">
              <div>
                <label className={labelClass}>Preferred Branch *</label>
                <select
                  name="btech_preffered_branch"
                  value={formData.btech_preffered_branch}
                  onChange={handleChange}
                  className={inputClass}
                  required
                >
                  <option value="">Select Preferred Branch</option>
                  <option value="CSE">Computer Science & Engineering (CSE)</option>
                  <option value="AI/ML">Artificial Intelligence & Machine Learning (AI/ML)</option>
                  <option value="ECE">Electronics & Communication Engineering (ECE)</option>
                  <option value="Aeronautical">Aeronautical Engineering</option>
                  <option value="BBA">BBA</option>
                  <option value="BA">BA</option>
                  <option value="LLB">LLB</option>
                </select>
              </div>
            </div>
          </section>

          {/* PERSONAL DETAILS */}
          <section>
            <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Personal Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2">
                <label className={labelClass}>Full Name *</label>
                <input
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  className={isEditMode ? disabledInputClass : inputClass}
                  readOnly={isEditMode}
                  placeholder="Enter full name"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Category *</label>
                <select name="category" value={formData.category} onChange={handleChange} className={inputClass} required>
                  <option value="">Select Category</option>
                  <option value="OPEN">OPEN</option>
                  <option value="OBC">OBC</option>
                  <option value="SC">SC</option>
                  <option value="ST">ST</option>
                  <option value="VJ/DT">VJ/DT</option>
                  <option value="DT">DT</option>
                  <option value="NT-C">NT-C</option>
                  <option value="NT-B">NT-B</option>
                  <option value="NT-D">NT-D</option>
                  <option value="SBC">SBC</option>
                  <option value="EWS">EWS</option>
                  <option value="SEBC">SEBC</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Religion *</label>
                <select name="religion" value={formData.religion} onChange={handleChange} className={inputClass} required>
                  <option value="">Select Religion</option>
                  <option>Hindu</option>
                  <option>Muslim</option>
                  <option>Christian</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Caste *</label>
                <input
                  name="caste"
                  value={formData.caste}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="e.g. BC / OC / SC / ST"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Date of Birth *</label>
                <input
                  type="date"
                  name="dob"
                  value={formData.dob}
                  onChange={handleChange}
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Gender *</label>
                <select name="gender" value={formData.gender} onChange={handleChange} className={inputClass} required>
                  <option value="">Select Gender</option>
                  <option>Male</option>
                  <option>Female</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Mobile Number * (10 digits)</label>
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  name="mobile_number"
                  maxLength={10}
                  value={formData.mobile_number}
                  onChange={handleChange}
                  onKeyPress={handleNumberOnly}
                  className={isEditMode ? disabledInputClass : inputClass}
                  readOnly={isEditMode}
                  required
                  placeholder="10 digit number"
                />
              </div>
              <div>
                <label className={labelClass}>Email ID *</label>
                <input 
                  type="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  className={inputClass} 
                  required 
                  placeholder="student@example.com" 
                />
              </div>
              <div>
                <label className={labelClass}>UIDAI Number / Aadhaar No. * (12 digits)</label>
                <input
                  name="aadhar"
                  maxLength={12}
                  value={formData.aadhar}
                  onChange={handleChange}
                  className={isEditMode ? disabledInputClass : inputClass}
                  readOnly={isEditMode}
                  required
                  placeholder="12 digit Aadhaar number"
                />
              </div>
              <div>
                <label className={labelClass}>Student Photograph *</label>
                <div
                  className="border-2 border-dashed border-gray-300 rounded p-6 text-center cursor-pointer hover:border-blue-500"
                  onClick={() => photoRef.current.click()}
                >
                  <p className="text-sm text-gray-500">Click to upload photo (jpg/png)</p>
                  <input
                    ref={photoRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileChange(e, 'photograph', setPhotoPreview)}
                  />
                </div>
                {photoPreview && (
                  <div className="mt-4 flex flex-col items-center">
                    {getFilePreviewContent(photoPreview, 'Photograph')}
                  </div>
                )}
              </div>
              <div>
                <label className={labelClass}>Aadhaar Image *</label>
                <div
                  className="border-2 border-dashed border-gray-300 rounded p-6 text-center cursor-pointer hover:border-blue-500"
                  onClick={() => aadharRef.current.click()}
                >
                  <p className="text-sm text-gray-500">Click to upload Aadhaar Card (jpg/png/pdf)</p>
                  <input
                    ref={aadharRef}
                    type="file"
                    accept="image/*,.pdf"
                    className="hidden"
                    onChange={(e) => handleFileChange(e, 'aadhar_image', setAadharPreview)}
                  />
                </div>
                {aadharPreview && (
                  <div className="mt-4 flex flex-col items-center">
                    {getFilePreviewContent(aadharPreview, 'Aadhaar')}
                  </div>
                )}
              </div>
              <div>
                <label className={labelClass}>Mother's Name *</label>
                <input 
                  name="mother_name" 
                  value={formData.mother_name} 
                  onChange={handleChange} 
                  className={inputClass} 
                  required 
                  placeholder="Enter mother's name"
                />
              </div>
              <div>
                <label className={labelClass}>Parents Mobile Number * (10 digits)</label>
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  name="parent_mobile_number"
                  maxLength={10}
                  value={formData.parent_mobile_number}
                  onChange={handleChange}
                  onKeyPress={handleNumberOnly}
                  className={inputClass}
                  required
                  placeholder="10 digit number"
                />
              </div>
            </div>
          </section>

          {/* ADDRESS DETAILS */}
          <section>
            <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Address Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-3">
                <label className={labelClass}>Correspondence Address *</label>
                <input
                  name="address_line1"
                  value={formData.address_line1}
                  onChange={handleChange}
                  placeholder="House no, Street, Area, Landmark..."
                  className={inputClass}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>State *</label>
                <input 
                  name="state" 
                  required 
                  value={formData.state} 
                  onChange={handleChange} 
                  className={inputClass} 
                  placeholder="e.g. Andhra Pradesh" 
                />
              </div>
              <div>
                <label className={labelClass}>City *</label>
                <input 
                  name="city" 
                  required 
                  value={formData.city} 
                  onChange={handleChange} 
                  className={inputClass} 
                  placeholder="e.g. Vijayawada" 
                />
              </div>
              <div>
                <label className={labelClass}>PIN Code * (6 digits)</label>
                <input 
                  name="pincode" 
                  value={formData.pincode} 
                  onChange={handleChange} 
                  className={inputClass} 
                  maxLength={6} 
                  placeholder="e.g. 520001"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>District *</label>
                <input 
                  name="district" 
                  required 
                  value={formData.district} 
                  onChange={handleChange} 
                  className={inputClass} 
                  placeholder="e.g. NTR / Krishna" 
                />
              </div>
            </div>
          </section>

          {/* Xth DETAILS */}
          <section>
            <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Xth Details (SSC)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className={labelClass}>Board *</label>
                <input
                  name="tenth_board"
                  value={formData.tenth_board}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      tenth_board: e.target.value.replace(/[^a-zA-Z\s]/g, ''),
                    })
                  }
                  className={inputClass}
                  placeholder="e.g. Andhra Pradesh Board Of Secondary Education"
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Year of Passing *</label>
                <input 
                  name="tenth_year_passing" 
                  value={formData.tenth_year_passing} 
                  onChange={handleChange} 
                  className={inputClass} 
                  maxLength={4}
                  placeholder="e.g. 2024" 
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Percentage *</label>
                <input 
                  name="tenth_percentage" 
                  value={formData.tenth_percentage} 
                  onChange={handleChange} 
                  className={inputClass} 
                  placeholder="e.g. 8.5" 
                  required
                />
              </div>
              <div>
                <label className={labelClass}>SSC Marks Memo *</label>
                <div
                  className="border-2 border-dashed border-gray-300 rounded p-6 text-center cursor-pointer hover:border-blue-500 transition"
                  onClick={() => sscMemoRef.current.click()}
                >
                  <p className="text-sm text-gray-500">
                    Upload SSC Memo (PDF / Image)
                  </p>
                  <input
                    ref={sscMemoRef}
                    type="file" 
                    accept=".pdf,image/*"
                    className="hidden"
                    onChange={(e) => handleFileChange(e, 'ssc_memo', setSscMemoPreview)}
                  />
                </div>
                {sscMemoPreview && (
                  <div className="mt-5 flex flex-col items-center">
                    {getFilePreviewContent(sscMemoPreview, 'SSC Memo', formData.ssc_memo?.name)}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* XIIth / INTERMEDIATE DETAILS */}
          <section>
            <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">XIIth Details (Intermediate)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className={labelClass}>Board *</label>
                <select 
                  name="twelfth_board_name" 
                  value={formData.twelfth_board_name} 
                  onChange={handleChange} 
                  className={inputClass}
                  required
                >
                  <option value="">Select Board</option>
                  <option>Board of Intermediate Education, Andhra Pradesh</option>
                      <option>Board of Intermediate Education, Telangana</option>
                  <option>CBSE</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Year of Passing *</label>
                <select
                  name="twelfth_year_passing"
                  value={formData.twelfth_year_passing}
                  onChange={handleChange}
                  className={inputClass}
                  required
                >
                  <option value="">Select Year</option>
                  <option value="2026">2026</option>
                  <option value="2025">2025</option>
                  <option value="2024">2024</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Percentage / CGPA *</label>
                <input 
                  name="twelfth_percentage_cgpa" 
                  value={formData.twelfth_percentage_cgpa} 
                  onChange={handleChange} 
                  className={inputClass} 
                  placeholder="e.g. 9.24" 
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Branch *</label>
                <select 
                  name="twelfth_branch" 
                  value={formData.twelfth_branch} 
                  onChange={handleChange} 
                  className={inputClass}
                  required
                >
                  <option value="">Select Branch</option>
                  <option>Science</option>
                  <option>Commerce</option>
                  <option>Arts</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Inter 1st Year Marks Memo *</label>
                <div
                  className="border-2 border-dashed border-gray-300 rounded p-6 text-center cursor-pointer hover:border-blue-500 transition"
                  onClick={() => firstYearMemoRef.current.click()}
                >
                  <p className="text-sm text-gray-500">
                    Upload 1st Year Memo (PDF / Image)
                  </p>
                  <input
                    ref={firstYearMemoRef}
                    type="file"
                    accept=".pdf,image/*"
                    className="hidden"
                    onChange={(e) => handleFileChange(e, 'inter_first_year_memo', setFirstMemoPreview)}
                  />
                </div>
                {firstMemoPreview && (
                  <div className="mt-5 flex flex-col items-center">
                    {getFilePreviewContent(firstMemoPreview, '1st Year Memo', formData.inter_first_year_memo?.name)}
                  </div>
                )}
              </div>
              <div>
                <label className={labelClass}>Inter 2nd Year Marks Memo *</label>
                <div
                  className="border-2 border-dashed border-gray-300 rounded p-6 text-center cursor-pointer hover:border-blue-500 transition"
                  onClick={() => secondYearMemoRef.current.click()}
                >
                  <p className="text-sm text-gray-500">
                    Upload 2nd Year Memo (PDF / Image) 
                  </p>
                  <input
                    ref={secondYearMemoRef}
                    type="file"
                    accept=".pdf,image/*"
                    className="hidden"
                    onChange={(e) => handleFileChange(e, 'inter_second_year_memo', setSecondMemoPreview)}
                  />
                </div>
                {secondMemoPreview && (
                  <div className="mt-5 flex flex-col items-center">
                    {getFilePreviewContent(secondMemoPreview, '2nd Year Memo', formData.inter_second_year_memo?.name)}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* ENTRANCE EXAM DETAILS */}
          <section>
            <h3 className="text-xl font-semibold text-gray-800 mb-5 border-b pb-2">Entrance Exam Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-xl">
              <div>
                <label className={labelClass}>Entrance Exam Name *</label>
                <input
                  name="entrance_exam_name"
                  value={formData.entrance_exam_name}
                  onChange={(e) => {
                    const onlyChars = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                    handleChange({
                      target: {
                        name: "entrance_exam_name",
                        value: onlyChars
                      }
                    });
                  }}
                  placeholder="e.g. AP EAMCET, JEE Main, NEET, Others"
                  className={inputClass}
                  required
                />
              </div>
            </div>
          </section>

          {/* DECLARATION */}
          <section className="border-t pt-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="declaration_agreed"
                checked={formData.declaration_agreed}
                onChange={handleChange}
                className="mt-1 w-5 h-5 accent-blue-600"
                required
              />
              <span className="text-sm text-gray-700 leading-relaxed">
                I hereby declare that the information furnished by me in the admission application submitted to Sanjay Ghodawat University Kolhapur is true and correct to the best of my knowledge and belief.
              </span>
            </label>
          </section>

          {/* SUBMIT BUTTON */}
          <div className="text-end mt-10">
            <button
              type="submit"
              className={`px-12 py-3 rounded-lg bg-blue-700 hover:bg-blue-800 text-white font-semibold text-lg transition shadow-md ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
              disabled={loading}
            >
              {loading
                ? 'Processing...'
                : isEditMode || isRegistered
                ? 'UPDATE STUDENT'
                : 'NEXT'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default StudentRegistration;